/**
 * useOperatorDay — games assigned to the current operator for a single day.
 *
 * Wraps GET /api/assignments?date=&operator_id= (forked v3 route). For an
 * operator the route auto-scopes to self, hides unpublished future schedules,
 * and enriches each row with colleagues on the same booking. We map the raw
 * rows to a compact GameView the B.9 dashboard renders.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../api/client";
import type { ApiList } from "../api/types";
import { fmtDateApi, addDays, isNightTail } from "../utils/date";
import type { ReportPrefill } from "../screens/op/reportModel";

export type GameStatus = "assigned" | "accepted" | "reported" | "finished";

export interface GameView {
  bookingId: number;
  /** id назначения — кнопка «Принять игру» (26.08, владелец). */ assignmentId: number | null;
  time: string;
  questShort: string;
  questName: string;
  players: number;
  tea: boolean;
  prepaid: boolean;
  /** Сумма предоплаты (26.08, владелец): оператор видит, сколько внесено. */ prepaidAmount: number;
  /** КНКР — конкуренты на игре (27.08, владелец: крутящаяся лампа в дашборде) */ competition: boolean;
  /** Поздняя отмена (28.08): игроки не пришли — игра видна, отчёт сдаётся */ cancelled: boolean; cancelReason: string;
  status: GameStatus;
  client: string | null;
  comment: string | null;
  teaRoom: string | null;
  buddies: string[];
  /* Поля для открытия формы отчёта прямо с дашборда. */
  source: string;
  mc: string | null;
  branchId: string | null;
  /* МК-исход из сданного отчёта + его id (26.08: кнопки 5.0/✕ в карточке). */
  reportId: number | null; mkStatus: string;
  /* Отчёт возвращён на исправление (26.08): игра снова «Сдать отчёт», форма — fix с prefill. */
  fixReportId?: number; fixPrefill?: { players: number; cash: number; terminal: number; transfer: number; prepaidUsed: number; invoice: number; activity: string; comment: string };
  /** Название спецверсии игры (26.08, владелец) — оператор обязан её видеть. */
  specialVersion: string | null;
}

interface RawRep {
  booking_id: number; id?: number; mk_status?: string | null; needs_correction?: number;
  actual_players?: number | null; cash?: number | null; terminal?: number | null; transfer?: number | null;
  prepaid_used?: number | null; invoice?: number | null; activity?: string | null; comment?: string | null;
}

interface RawRow {
  id?: number; booking_id: number;
  time: string; custom_time?: string | null;
  short_name?: string;
  quest_name?: string;
  players?: number;
  tea?: number;
  prepaid?: number;
  prepaid_amount?: number | null;
  is_competition?: number;
  b_status?: string;
  cancel_reason?: string | null;
  status?: string;
  client_name?: string | null;
  comment?: string | null;
  tea_room_id?: string | null;
  source?: string | null;
  mc?: string | null;
  branch_id?: string | null;
  special_version_title?: string | null;
  colleagues?: { colleague_name?: string }[] | string[];
}

function mapStatus(s: string | undefined): GameStatus {
  switch (s) {
    case "accepted":
      return "accepted";
    case "reported":
    case "done":
      return "reported";
    case "finished":
    case "completed":
      return "finished";
    default:
      return "assigned";
  }
}

function buddyNames(raw: RawRow["colleagues"]): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c) => (typeof c === "string" ? c : c?.colleague_name))
    .filter((n): n is string => !!n);
}

export interface OperatorDayResult {
  games: GameView[];
  loading: boolean;
  error: string | null;
  /** Смена на этот день назначена, но ещё не отправлена оператору (11.09). */
  draftPending: boolean;
  reload: () => void;
}

export function useOperatorDay(date: Date, operatorId?: string): OperatorDayResult {
  const [games, setGames] = useState<GameView[]>([]);
  const [draftPending, setDraftPending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);

    const d = fmtDateApi(date);
    /* 27.08 (владелец): смена дня включает ночной хвост D+1 (игры до 06:00),
       а свои ночные игры принадлежат вчерашней смене */
    const dNext = fmtDateApi(addDays(date, 1));
    const qs = new URLSearchParams({ date: d });
    const qsN = new URLSearchParams({ date: dNext });
    if (operatorId) { qs.set("operator_id", operatorId); qsN.set("operator_id", operatorId); }

    Promise.all([
      api<ApiList<RawRow> & { draft_pending?: boolean }>(`/api/assignments?${qs.toString()}`, { signal: ac.signal }),
      // Свои сданные отчёты за день (эндпоинт скоупится на оператора). Игра
      // считается «сдан» по НАЛИЧИЮ отчёта — assignments.status это не отражает,
      // иначе игра вечно висит в «Сдать отчёт» и оператор шлёт повторно.
      api<ApiList<RawRep>>(`/api/game-reports?date=${d}&mine=1`, { signal: ac.signal })
        .catch(() => ({ results: [] as RawRep[] })),
      api<ApiList<RawRow>>(`/api/assignments?${qsN.toString()}`, { signal: ac.signal }).catch(() => ({ results: [] as RawRow[] })),
      api<ApiList<RawRep>>(`/api/game-reports?date=${dNext}&mine=1`, { signal: ac.signal }).catch(() => ({ results: [] as RawRep[] })),
    ])
      .then(([resDay, repDay, resNext, repNext]) => {
        if (ac.signal.aborted) return;
        /* 11.09: смена на этот день назначена, но ОО её ещё не отправил — говорим об этом
           вместо «игр нет», иначе человек думает, что его забыли поставить. */
        setDraftPending(!!resDay.draft_pending);
        const tOf = (r: RawRow) => ((r.custom_time || r.time || "") as string).slice(0, 5);
        const res = { results: [
          ...(resDay.results ?? []).filter((r) => !isNightTail(tOf(r))),
          ...(resNext.results ?? []).filter((r) => isNightTail(tOf(r))),
        ] };
        const nightIds = new Set(res.results.filter((r) => isNightTail(tOf(r))).map((r) => Number(r.booking_id)));
        const rep = { results: [
          ...(repDay.results ?? []),
          ...(repNext.results ?? []).filter((r) => nightIds.has(Number(r.booking_id))),
        ] };
        const reported = new Map<number, { id: number | null; mk: string }>();
        /* needs_correction=1 → НЕ «сдан»: игра вернётся в «Сдать отчёт», форма fix с prefill (26.08) */
        const fixes = new Map<number, { id: number; prefill: NonNullable<GameView["fixPrefill"]> }>();
        for (const r of rep.results ?? []) {
          if (r.needs_correction) {
            if (r.id != null) fixes.set(Number(r.booking_id), { id: r.id, prefill: {
              players: Number(r.actual_players ?? 0), cash: Number(r.cash ?? 0), terminal: Number(r.terminal ?? 0),
              transfer: Number(r.transfer ?? 0), prepaidUsed: Number(r.prepaid_used ?? 0), invoice: Number(r.invoice ?? 0),
              activity: r.activity ?? "", comment: r.comment ?? "",
            } });
            continue;
          }
          reported.set(Number(r.booking_id), { id: r.id ?? null, mk: r.mk_status ?? "" });
        }
        const mapped: GameView[] = (res.results ?? []).map((r) => ({
          bookingId: r.booking_id,
          assignmentId: r.id != null && (r.status ?? "assigned") === "assigned" ? Number(r.id) : null, /* только сырой assigned: иначе кнопка «Принять» откатывала бы started/no_show/cancelled */
          /* 26.08 (владелец): кастомное время игры, не время слота */
          time: (r.custom_time || r.time || "").slice(0, 5),
          /* 22.09 (владелец): игра называется спецверсией — базовый квест больше не показываем
             (раньше в карточке стояли оба имени сразу). */
          questShort: r.special_version_title || r.short_name || r.quest_name || "—",
          questName: r.special_version_title || r.quest_name || r.short_name || "—",
          players: Number(r.players ?? 0),
          tea: !!r.tea,
          prepaid: !!r.prepaid,
          prepaidAmount: Number(r.prepaid_amount ?? 0),
          competition: !!r.is_competition,
          cancelled: r.b_status === "cancelled",
          cancelReason: r.cancel_reason ?? "",
          status: reported.has(Number(r.booking_id)) ? "reported" : mapStatus(r.status),
          client: r.client_name ?? null,
          comment: r.comment ?? null,
          teaRoom: r.tea_room_id ?? null,
          buddies: buddyNames(r.colleagues),
          source: r.source ?? "",
          mc: r.mc ?? null,
          branchId: r.branch_id ?? null,
          reportId: reported.get(Number(r.booking_id))?.id ?? null,
          mkStatus: reported.get(Number(r.booking_id))?.mk ?? "",
          fixReportId: fixes.get(Number(r.booking_id))?.id,
          fixPrefill: fixes.get(Number(r.booking_id))?.prefill,
          specialVersion: r.special_version_title ?? null,
        }));
        /* ночной хвост — в конец смены (часы +24 в ключе сортировки) */
        const sk = (t: string) => (isNightTail(t) ? `${String(Number(t.slice(0, 2)) + 24)}:${t.slice(3)}` : t);
        mapped.sort((a, b) => sk(a.time).localeCompare(sk(b.time)));
        setGames(mapped);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setError(e instanceof Error ? e.message : "Не удалось загрузить игры");
        setLoading(false);
      });

    return () => ac.abort();
  }, [date, operatorId, trigger]);

  return { games, loading, error, draftPending, reload };
}

/** Игра в списке «за всё время»: поля для открытия формы + (если есть) свой отчёт. */
export interface BacklogItem {
  bookingId: number;
  date: string;
  time: string;
  questShort: string;
  questName: string;
  players: number;
  tea: boolean;
  teaRoom: string | null;
  source: string;
  mc: string | null;
  branchId: string | null;
  client: string | null;
  hasReport: boolean;
  reportId?: number;
  prefill?: ReportPrefill;
  specialVersion?: string | null;
}

interface BacklogRow {
  booking_id: number;
  date?: string;
  time?: string;
  custom_time?: string | null;
  short_name?: string;
  quest_name?: string;
  players?: number;
  tea?: number;
  tea_room_id?: string | null;
  source?: string | null;
  mc?: string | null;
  branch_id?: string | null;
  client_name?: string | null;
  report_id?: number | null;
  actual_players?: number | null;
  cash?: number | null;
  terminal?: number | null;
  transfer?: number | null;
  prepaid_used?: number | null;
  invoice?: number | null;
  activity?: string | null;
  report_comment?: string | null;
}

export interface OperatorBacklogResult {
  items: BacklogItem[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * useOperatorBacklog — ВСЕ прошедшие игры оператора за всё время одним списком
 * (в отличие от дневного useOperatorDay). Со сданным отчётом или без: для сданных
 * приходит report_id + prefill, чтобы открыть форму в режиме правки. Ленивый —
 * грузит только когда `enabled` (раскрыта секция).
 */
export function useOperatorBacklog(operatorId: string | undefined, enabled: boolean): OperatorBacklogResult {
  const [items, setItems] = useState<BacklogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);

    api<ApiList<BacklogRow>>(`/api/game-reports/my-backlog`, { signal: ac.signal })
      .then((res) => {
        if (ac.signal.aborted) return;
        const mapped: BacklogItem[] = (res.results ?? []).map((r) => {
          const hasReport = r.report_id != null;
          return {
            bookingId: r.booking_id,
            date: (r.date ?? "").slice(0, 10),
            time: (r.custom_time || r.time || "").slice(0, 5),
            questShort: (r as { special_version_title?: string | null }).special_version_title || r.short_name || r.quest_name || "—",
            questName: (r as { special_version_title?: string | null }).special_version_title || r.quest_name || r.short_name || "—",
            players: Number(r.players ?? 0),
            tea: !!r.tea,
            teaRoom: r.tea_room_id ?? null,
            source: r.source ?? "",
            mc: r.mc ?? null,
            branchId: r.branch_id ?? null,
            client: r.client_name ?? null,
            specialVersion: (r as { special_version_title?: string | null }).special_version_title ?? null,
            hasReport,
            reportId: r.report_id ?? undefined,
            prefill: hasReport
              ? {
                  players: Number(r.actual_players ?? r.players ?? 0),
                  cash: Number(r.cash ?? 0),
                  terminal: Number(r.terminal ?? 0),
                  transfer: Number(r.transfer ?? 0),
                  prepaidUsed: Number(r.prepaid_used ?? 0),
                  invoice: Number(r.invoice ?? 0),
                  activity: r.activity ?? "",
                  comment: r.report_comment ?? "",
                }
              : undefined,
          };
        });
        setItems(mapped);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setError(e instanceof Error ? e.message : "Не удалось загрузить игры");
        setLoading(false);
      });

    return () => ac.abort();
  }, [operatorId, enabled, trigger]);

  return { items, loading, error, reload };
}

/** «Также сегодня» у оператора (26.08, владелец): админство зала (филиал +
 *  часы из hall_admin_times) и допработы дня — раньше жили только в
 *  расстановке ОО, оператор их в кабинете не видел. Best-effort. */
export interface SideDuties { admin: { branch: string; from: string | null; to: string | null; repFrom: string | null; repTo: string | null } | null; extras: { id: number; from: string; to: string; desc: string; accepted: boolean; branch: string | null; taxi: number; reportedAt: string | null; factFrom: string | null; factTo: string | null; factOk: boolean; reportText: string | null; taxiPaidBy: string | null; study: boolean; curator: string | null }[]; reloadSide: () => void }

export function useOperatorSideDuties(date: Date, operatorId: string | undefined): SideDuties {
  const [tick, setTick] = useState(0);
  const reloadSide = useCallback(() => setTick((n) => n + 1), []);
  const [side, setSide] = useState<SideDuties>({ admin: null, extras: [], reloadSide });
  useEffect(() => {
    if (!operatorId) return;
    const ac = new AbortController();
    const d = fmtDateApi(date);
    Promise.all([
      api<{ hall_admins?: Record<string, string[]>; hall_admin_times?: Record<string, { from: string | null; to: string | null }>; hall_admin_state?: Record<string, Record<string, { sent: boolean }>> }>(`/api/assignments/meta?date=${d}`, { signal: ac.signal }).catch(() => ({}) as { hall_admins?: Record<string, string[]> }),
      api<{ results?: { id?: number; operator_id?: string; time_from?: string; time_to?: string; description?: string; accepted_at?: string | null; branch_id?: string | null; taxi?: number | null; reported_at?: string | null; fact_from?: string | null; fact_to?: string | null; fact_ok?: number | null; report_text?: string | null; taxi_paid_by?: string | null; kind?: string | null; curator_name?: string | null; draft?: number | null }[] }>(`/api/extra-work?date=${d}`, { signal: ac.signal }).catch(() => ({ results: [] })),
      api<{ time_from?: string; time_to?: string } | null>(`/api/hall-admin-reports?date=${d}&operator_id=${operatorId}`, { signal: ac.signal }).catch(() => null),
    ]).then(([meta, ew, rep]) => {
      if (ac.signal.aborted) return;
      let admin: SideDuties["admin"] = null;
      for (const [br, ids] of Object.entries(meta.hall_admins ?? {})) {
        if ((Array.isArray(ids) ? ids : [ids]).includes(operatorId) && (!("hall_admin_state" in meta) || meta.hall_admin_state?.[br]?.[operatorId]?.sent)) {   // 28.09: офису meta отдаёт и черновик
          const t = (meta as { hall_admin_times?: Record<string, { from: string | null; to: string | null }> }).hall_admin_times?.[br];
          admin = { branch: br, from: t?.from ?? null, to: t?.to ?? null, repFrom: rep?.time_from?.slice(0, 5) ?? null, repTo: rep?.time_to?.slice(0, 5) ?? null };
        }
      }
      /* 27.09 (владелец): неотправленный черновик человек не видит — ЕО/ОО сервер отдаёт всё,
         и в своём кабинете они видели чужую «Сохранить» как назначенную работу */
      const extras = (ew.results ?? []).filter((r) => r.operator_id === operatorId && !r.draft)
        .map((r) => ({
          id: Number(r.id ?? 0), from: (r.time_from ?? "").slice(0, 5), to: (r.time_to ?? "").slice(0, 5),
          desc: r.description ?? "", accepted: !!r.accepted_at, branch: r.branch_id ?? null,
          /* 11.09: отчёт о работе живёт и здесь — на дашборде человек её и видит. */
          taxi: Number(r.taxi ?? 0), reportedAt: r.reported_at ?? null,
          factFrom: r.fact_from ? String(r.fact_from).slice(0, 5) : null,
          factTo: r.fact_to ? String(r.fact_to).slice(0, 5) : null,
          factOk: !!r.fact_ok, reportText: r.report_text ?? null, taxiPaidBy: r.taxi_paid_by ?? null,
          study: r.kind === "training", curator: r.curator_name ?? null,
        }));
      setSide({ admin, extras, reloadSide });
    }).catch(() => {});
    return () => ac.abort();
  }, [date, operatorId, tick, reloadSide]);
  return side;
}
